import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Typography,
	Chip,
	IconButton,
	CircularProgress,
	Switch,
	FormControlLabel,
	TextField,
	Button,
	Select,
	MenuItem,
	FormControl,
	Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LockIcon from "@mui/icons-material/Lock";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import {
	IonButton,
	IonButtons,
	IonContent,
	IonFooter,
	IonHeader,
	IonIcon,
	IonModal,
	IonTitle,
	IonToolbar,
} from "@ionic/react";
import { chatbubbleOutline, attach, createOutline, closeOutline, linkOutline, checkmarkDoneOutline, refreshOutline,trashOutline } from "ionicons/icons";
import "./PastoralNoteDrawer.css";
import { HtmlText, FileUploadZone, displayMsg } from "pymble-ui/components";
import {
	PastoralNote,
	PastoralNoteFile,
	usePastoralCategories,
	usePastoralNoteComments,
  useSetNoteStatus,
	useUpdateNoteFiles,
	useUpdatePastoralNote,
	useAddNoteComment,
	useUpdateNoteComment,
	useDeleteNoteComment,
	useCreatePastoralNote,
	useDeletePastoralNote,
	canEditEntity,
	canDeleteEntity,
	PREDEFINED_TAG_NAMES,
	tagColour,
	ACTIONED_CP_TAG,
	CHILD_PROTECTION_TAG,
} from "../../hooks/usePastoralNotes";
import type { RawNoteStatus } from "../../hooks/usePastoralNotes";
import NoteStatusChip from "./NoteStatusChip";
import { useNoteFileUpload } from "../../hooks/useNoteFileUpload";
import { usePymbleApi } from "../../hooks/usePymbleApi";
import { useMyDetails } from "../../hooks/useMyDetails";
import { useStreamingAgent } from "../../hooks/useStreamingAgent";
import { agents } from "../../config";
import { formatDate } from "../../utils";
import NoteVisibilityIndicator from "../teacher/NoteVisibilityIndicator";
import ContactProfileImage from "../common/ContactProfileImage";

/**
 * Build the "Uploaded by …" caption from a file's server-resolved uploader
 * details. Returns null when there's no resolved uploader, so the caller can
 * skip rendering it.
 */
const fileUploaderCaption = (file: PastoralNoteFile): string | null => {
	const name = file.UploadedByDetail?.DescLong;
	if (!name) return null;
	const when = file.UploadedAt ? formatDate(file.UploadedAt) : "";
	return when ? `Uploaded by ${name} · ${when}` : `Uploaded by ${name}`;
};

interface PastoralNoteDrawerProps {
	note: PastoralNote | null;
	onClose: () => void;
	isAdding?: boolean;
	studentId?: string;
	studentName?: string;
	onAddClose?: () => void;
}

const PastoralNoteDrawer: React.FC<PastoralNoteDrawerProps> = ({
	note,
	onClose,
	isAdding = false,
	studentId,
	studentName,
	onAddClose,
}) => {
	const { callApi } = usePymbleApi();

	// Copy a shareable deep-link to this note. The link carries ?student= so the
	// recipient's grid resolves the fully-enriched note from the context feed,
	// and ?note= auto-opens the drawer. Both are opaque Mongo ids — no PII.
	const handleCopyLink = async () => {
		if (!note) return;
		const url = `${window.location.origin}${window.location.pathname}?student=${note.student_id}&note=${note.id}`;
		try {
			await navigator.clipboard.writeText(url);
			displayMsg("Link copied to clipboard.", { type: "success", position: "bottom", duration: 2000 });
		} catch {
			displayMsg("Could not copy link.", { type: "danger", position: "bottom", duration: 3000 });
		}
	};
	const { myDetails } = useMyDetails();

	// Only the note's author may edit or delete it. Resolved by canEditEntity,
	// which prefers the server's CanEdit and falls back through the author
	// heuristics. This is a UX gate only — the API enforces authorship.
	const isAuthor = canEditEntity(note, myDetails);
	const canDeleteNote = canDeleteEntity(note, myDetails);

	// Inline AI note assist: stream a drafted/expanded note from the student agent
	// and inject it into whichever field triggered it (new vs edit).
	const { streamingData, connectionClosed, retrieveStreamingData } = useStreamingAgent();
	const [isGeneratingAi, setIsGeneratingAi] = useState(false);
	const aiTargetRef = useRef<"new" | "edit" | null>(null);

	useEffect(() => {
		if (streamingData && streamingData.length > 0) {
			const text = streamingData.join("");
			if (aiTargetRef.current === "new") setNewNoteText(text);
			else if (aiTargetRef.current === "edit") setEditNoteText(text);
		}
	}, [streamingData]);

	useEffect(() => {
		if (connectionClosed) setIsGeneratingAi(false);
	}, [connectionClosed]);

	const handleAiGenerate = async (mode: "new" | "edit") => {
		const currentText = mode === "new" ? newNoteText : editNoteText;
		if (!currentText.trim() || isGeneratingAi) return;
		aiTargetRef.current = mode;
		setIsGeneratingAi(true);
		try {
			await retrieveStreamingData(
				{
					Student: note?.student_name || studentName || "this student",
					StudentId: note?.student_id || studentId,
					SenderId: myDetails?._id,
					Topic: currentText,
				},
				agents.STUDENT_AGENT_ID
			);
		} catch {
			setIsGeneratingAi(false);
		}
	};

	const { data: comments, isLoading: commentsLoading } = usePastoralNoteComments(note?.id ?? null);
	const { uploadFile, isUploading, deleteFile, isDeleting } = useNoteFileUpload();
	const { mutate: updateNoteFiles } = useUpdateNoteFiles();
	const { mutateAsync: addComment } = useAddNoteComment();
	const { mutateAsync: updateComment } = useUpdateNoteComment();
	const { mutateAsync: deleteComment } = useDeleteNoteComment();
	const { mutateAsync: setNoteStatus, isPending: isSettingStatus } = useSetNoteStatus();

	const { data: categories } = usePastoralCategories(note?.student_id ?? studentId);
	const { mutateAsync: updateNote, isPending: isSaving } = useUpdatePastoralNote();
	const { mutateAsync: createNote, isPending: isCreating } = useCreatePastoralNote();
	const { mutateAsync: deleteNote, isPending: isDeletingNote } = useDeletePastoralNote();

	const [isEditing, setIsEditing] = useState(false);
	const [editNoteText, setEditNoteText] = useState("");
	const [editCategoryId, setEditCategoryId] = useState("");
	const [editTags, setEditTags] = useState<string[]>([]);

	const [newNoteText, setNewNoteText] = useState("");
	const [newCategoryId, setNewCategoryId] = useState("");
	const [newTags, setNewTags] = useState<string[]>([]);
	const [newPendingFiles, setNewPendingFiles] = useState<File[]>([]);
	const [newUploadedFiles, setNewUploadedFiles] = useState<PastoralNoteFile[]>([]);
	const newUploadedRef = useRef<PastoralNoteFile[]>([]);
	newUploadedRef.current = newUploadedFiles;

	const [commentsOpen, setCommentsOpen] = useState(false);
	const [attachmentsOpen, setAttachmentsOpen] = useState(false);
	const [showSystemComments, setShowSystemComments] = useState(false);

	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const [localFiles, setLocalFiles] = useState<PastoralNoteFile[] | null>(null);
	const localFilesRef = useRef<PastoralNoteFile[] | null>(null);
	localFilesRef.current = localFiles;

	// Container SAS query string used to load secure image thumbnails inline, and
	// the set of files whose thumbnail failed to load (so we fall back to an icon).
	const [thumbSas, setThumbSas] = useState<string | null>(null);
	const [brokenThumbs, setBrokenThumbs] = useState<Set<string>>(new Set());
	const commentInputRef = useRef<HTMLTextAreaElement | null>(null);

	const [commentText, setCommentText] = useState("");
	const [commentConfidential, setCommentConfidential] = useState(false);
	const [commentPendingFiles, setCommentPendingFiles] = useState<File[]>([]);
	const [commentUploadedFiles, setCommentUploadedFiles] = useState<PastoralNoteFile[]>([]);
	const commentUploadedRef = useRef<PastoralNoteFile[]>([]);
	commentUploadedRef.current = commentUploadedFiles;
	const [showCommentUpload, setShowCommentUpload] = useState(false);
	const [isSubmittingComment, setIsSubmittingComment] = useState(false);

	// Inline comment edit/delete. Only the comment's author sees these controls
	// (UX gate; the API enforces authorship). `editingCommentId` drives the
	// inline editor, `savingCommentId`/`deletingCommentId` drive per-row spinners.
	const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
	const [editingCommentText, setEditingCommentText] = useState("");
	const [savingCommentId, setSavingCommentId] = useState<string | null>(null);
	const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

	useEffect(() => {
		setCommentsOpen(false);
		setAttachmentsOpen(false);
		setShowSystemComments(false);
		setLocalFiles(null);
		setPendingFiles([]);
		setThumbSas(null);
		setBrokenThumbs(new Set());
		setCommentText("");
		setCommentConfidential(false);
		setCommentPendingFiles([]);
		setCommentUploadedFiles([]);
		commentUploadedRef.current = [];
		setShowCommentUpload(false);
		setEditingCommentId(null);
		setEditingCommentText("");
		setSavingCommentId(null);
		setDeletingCommentId(null);
		setIsEditing(false);
		setEditNoteText("");
		setEditCategoryId("");
		setEditTags([]);
	}, [note?.id]);

	const displayFiles = localFiles ?? note?.files ?? [];
	const isChildProtection = note?.tags.some((t) => t.id === CHILD_PROTECTION_TAG) ?? false;

	// CP lifecycle control (mirrors my-pymble-portal's student context, where
	// only child-protection notes surface a status). Toggle between Actioned
	// (+ ActionedCP tag) and Viewed (tag removed).
	//
	// The status is held locally and updated from the API response rather than
	// read straight off the `note` prop. The prop is the row object captured
	// when the note was opened, and the mutation's cache invalidation cannot
	// always refresh it: a note reached by a ?note= deep link never came from
	// the feed at all, and a note outside the loaded pages or the active filter
	// is absent from the refetched feed. In both cases the prop keeps its
	// pre-mutation value, so the button would never change.
	const [savedStatus, setSavedStatus] = useState<RawNoteStatus | undefined>(note?.raw_status);

	// Re-seed when a different note is opened, or when the prop does refresh
	// (feed-resident notes), so the prop stays the source of truth on load.
	useEffect(() => {
		setSavedStatus(note?.raw_status);
	}, [note?.id, note?.raw_status]);

	const isActioned = savedStatus === "Actioned";

	const handleToggleActioned = async () => {
		if (!note) return;
		const tagNames = note.tags.map((t) => t.name);
		const nextStatus: RawNoteStatus = isActioned ? "Viewed" : "Actioned";
		const nextTags = isActioned
			? tagNames.filter((t) => t !== ACTIONED_CP_TAG)
			: tagNames.includes(ACTIONED_CP_TAG) ? tagNames : [...tagNames, ACTIONED_CP_TAG];
		try {
			const response = await setNoteStatus({ id: note.id, status: nextStatus, tags: nextTags });
			// Take the status the API saved, falling back to what we asked for if
			// the response does not echo it.
			setSavedStatus(response?.note?.Status ?? nextStatus);
			displayMsg(isActioned ? "Note reopened." : "Note marked as actioned.", { type: "success", position: "bottom", duration: 2000 });
		} catch {
			displayMsg("Could not update status. Please try again.", { type: "danger", position: "bottom", duration: 3000 });
		}
	};

	const systemCommentCount = comments?.filter((c) => c.isSystem).length ?? 0;
	const visibleComments = comments
		?.filter((c) => showSystemComments || !c.isSystem)
		.slice()
		.sort((a, b) => a.date.localeCompare(b.date));

	const openFile = async (file: PastoralNoteFile) => {
		let url = file.url;
		if (file.secure || url.includes("blob.core.windows.net")) {
			try {
				const sas = await callApi("GET", "storage/sas/", {
					params: { container: "secure", expiry: 60 },
				});
				if (sas?.SasQueryString) {
					url = `${url}?${sas.SasQueryString}`;
				}
			} catch (e) {
				console.error("Could not get SAS token for attachment", e);
			}
		}
		window.open(url, "_blank", "noopener,noreferrer");
	};

	const isPdf = (file: PastoralNoteFile) =>
		file.type?.toLowerCase() === "pdf" ||
		file.type?.toLowerCase().includes("pdf") ||
		file.name?.toLowerCase().endsWith(".pdf");

	const isImage = (file: PastoralNoteFile) => {
		const type = file.type?.toLowerCase() ?? "";
		if (type.startsWith("image") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(type)) {
			return true;
		}
		return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name ?? "");
	};

	// Secure blobs need a SAS query string appended to be viewable.
	const needsSas = (file: PastoralNoteFile) =>
		!!file.secure || !!file.url?.includes("blob.core.windows.net");

	const thumbKey = (file: PastoralNoteFile) => file.InternalName ?? file.url ?? file.name;

	const thumbUrl = (file: PastoralNoteFile) =>
		needsSas(file) && thumbSas ? `${file.url}?${thumbSas}` : file.url;

	// Show an inline thumbnail only for images that (a) aren't marked broken and
	// (b) have a usable URL — secure ones wait for the SAS token to arrive.
	const canShowThumb = (file: PastoralNoteFile) =>
		isImage(file) && !brokenThumbs.has(thumbKey(file)) && (needsSas(file) ? !!thumbSas : true);

	// Fetch one container SAS token when the attachments modal opens and there's a
	// secure image to render; it's reused for every secure thumbnail in the list.
	useEffect(() => {
		if (!attachmentsOpen || thumbSas) return;
		if (!displayFiles.some((f) => isImage(f) && needsSas(f))) return;
		let cancelled = false;
		(async () => {
			try {
				const sas = await callApi("GET", "storage/sas/", {
					params: { container: "secure", expiry: 60 },
				});
				if (!cancelled && sas?.SasQueryString) setThumbSas(sas.SasQueryString);
			} catch (e) {
				console.error("Could not get SAS token for thumbnails", e);
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [attachmentsOpen, displayFiles.length, thumbSas]);

	const handleFileZoneChange = async (newFiles: File[]) => {
		const added = newFiles.filter((f) => !pendingFiles.includes(f));
		setPendingFiles(newFiles);
		if (added.length === 0 || !note) return;

		let runningFiles = localFilesRef.current ?? note.files;
		for (const file of added) {
			const result = await uploadFile(file, note.id);
			setPendingFiles((prev) => prev.filter((f) => f !== file));
			if (result) {
				runningFiles = [...runningFiles, result];
				setLocalFiles(runningFiles);
				updateNoteFiles({ id: note.id, files: runningFiles });
			} else {
				displayMsg(`Failed to upload "${file.name}". Please try again.`, {
					type: "danger",
					position: "bottom",
					duration: 3000,
				});
			}
		}
	};

	const handleDeleteFile = async (file: PastoralNoteFile) => {
		if (!note || !file.InternalName) return;
		const container = file.container ?? "secure/notes";
		const ok = await deleteFile(container, file.InternalName);
		if (ok) {
			const updated = (localFilesRef.current ?? note.files).filter(
				(f) => f.InternalName !== file.InternalName
			);
			setLocalFiles(updated);
			updateNoteFiles({ id: note.id, files: updated });
		} else {
			displayMsg(`Could not delete "${file.name}". Please try again.`, {
				type: "danger",
				position: "bottom",
				duration: 3000,
			});
		}
	};

	const handleCommentFileZoneChange = async (newFiles: File[]) => {
		const added = newFiles.filter((f) => !commentPendingFiles.includes(f));
		setCommentPendingFiles(newFiles);
		if (added.length === 0 || !note) return;

		let runningUploaded = commentUploadedRef.current;
		for (const file of added) {
			const result = await uploadFile(file, note.id);
			setCommentPendingFiles((prev) => prev.filter((f) => f !== file));
			if (result) {
				runningUploaded = [...runningUploaded, result];
				commentUploadedRef.current = runningUploaded;
				setCommentUploadedFiles(runningUploaded);
			} else {
				displayMsg(`Failed to upload "${file.name}". Please try again.`, {
					type: "danger",
					position: "bottom",
					duration: 3000,
				});
			}
		}
	};

	const handleRemoveCommentFile = (file: PastoralNoteFile) => {
		const updated = commentUploadedFiles.filter((f) => f.InternalName !== file.InternalName);
		commentUploadedRef.current = updated;
		setCommentUploadedFiles(updated);
	};

	const handleSubmitComment = async () => {
		if ((!commentText.trim() && commentUploadedFiles.length === 0) || !note) return;
		setIsSubmittingComment(true);
		try {
			await addComment({
				noteId: note.id,
				text: commentText,
				confidential: commentConfidential,
				files: commentUploadedFiles,
			});
			setCommentText("");
			setCommentConfidential(false);
			setCommentPendingFiles([]);
			setCommentUploadedFiles([]);
			commentUploadedRef.current = [];
			setShowCommentUpload(false);
			displayMsg("Comment added.", { type: "success", position: "bottom", duration: 2000 });
		} catch {
			displayMsg("Failed to add comment. Please try again.", {
				type: "danger",
				position: "bottom",
				duration: 3000,
			});
		} finally {
			setIsSubmittingComment(false);
		}
	};

	const handleStartEditComment = (comment: { id: string; text: string }) => {
		setEditingCommentId(comment.id);
		setEditingCommentText(comment.text);
	};

	const handleCancelEditComment = () => {
		setEditingCommentId(null);
		setEditingCommentText("");
	};

	const handleSaveComment = async () => {
		if (!note || !editingCommentId || !editingCommentText.trim()) return;
		setSavingCommentId(editingCommentId);
		try {
			await updateComment({ id: editingCommentId, noteId: note.id, text: editingCommentText });
			setEditingCommentId(null);
			setEditingCommentText("");
			displayMsg("Comment updated.", { type: "success", position: "bottom", duration: 2000 });
		} catch {
			displayMsg("Failed to update comment. Please try again.", {
				type: "danger",
				position: "bottom",
				duration: 3000,
			});
		} finally {
			setSavingCommentId(null);
		}
	};

	const handleDeleteComment = async (commentId: string) => {
		if (!note) return;
		const ok = window.confirm("Delete this comment? This can't be undone.");
		if (!ok) return;
		setDeletingCommentId(commentId);
		try {
			await deleteComment({ id: commentId, noteId: note.id });
			if (editingCommentId === commentId) handleCancelEditComment();
			displayMsg("Comment deleted.", { type: "success", position: "bottom", duration: 2000 });
		} catch {
			displayMsg("Failed to delete comment. Please try again.", {
				type: "danger",
				position: "bottom",
				duration: 3000,
			});
		} finally {
			setDeletingCommentId(null);
		}
	};

	// Deleting the note closes the drawer — the grid clears its selection, which
	// also drops ?note= from the URL so the deep link can't re-open a dead note.
	const handleDeleteNote = async () => {
		if (!note) return;
		const ok = window.confirm("Delete this note? This can't be undone.");
		if (!ok) return;
		try {
			await deleteNote(note.id);
			displayMsg("Note deleted.", { type: "success", position: "bottom", duration: 2000 });
			onClose();
		} catch {
			displayMsg("Failed to delete note. Please try again.", {
				type: "danger",
				position: "bottom",
				duration: 3000,
			});
		}
	};

	const attachmentCount = displayFiles.length;
	// Use the live comments query for the badge so it updates immediately after adding a comment
	const liveCommentCount =
		comments != null ? comments.filter((c) => !c.isSystem).length : note?.comment_count ?? 0;

	const stripHtmlToText = (html: string) => {
		const div = document.createElement("div");
		div.innerHTML = html;
		return div.textContent ?? div.innerText ?? "";
	};

	useEffect(() => {
		if (!isAdding) {
			setNewNoteText("");
			setNewCategoryId("");
			setNewTags([]);
			setNewPendingFiles([]);
			setNewUploadedFiles([]);
			newUploadedRef.current = [];
		}
	}, [isAdding]);

	const handleNewNoteFileZoneChange = async (newFiles: File[]) => {
		const added = newFiles.filter((f) => !newPendingFiles.includes(f));
		setNewPendingFiles(newFiles);
		if (added.length === 0 || !studentId) return;

		const tempId = `${studentId}-draft`;
		let runningUploaded = newUploadedRef.current;
		for (const file of added) {
			const result = await uploadFile(file, tempId);
			setNewPendingFiles((prev) => prev.filter((f) => f !== file));
			if (result) {
				runningUploaded = [...runningUploaded, result];
				newUploadedRef.current = runningUploaded;
				setNewUploadedFiles(runningUploaded);
			} else {
				displayMsg(`Failed to upload "${file.name}". Please try again.`, {
					type: "danger",
					position: "bottom",
					duration: 3000,
				});
			}
		}
	};

	const handleRemoveNewNoteFile = (file: PastoralNoteFile) => {
		const updated = newUploadedFiles.filter((f) => f.InternalName !== file.InternalName);
		newUploadedRef.current = updated;
		setNewUploadedFiles(updated);
	};

	const handleCreateNote = async () => {
		if (!newNoteText.trim() || !studentId) return;
		try {
			await createNote({
				studentId,
				noteText: newNoteText,
				categoryId: newCategoryId,
				tags: newTags,
				files: newUploadedFiles,
			});
			displayMsg("Note created.", { type: "success", position: "bottom", duration: 2000 });
			onAddClose?.();
		} catch (e: unknown) {
			let msg = "Failed to create note. Please try again.";
			if (e && typeof e === "object" && "response" in e) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const axiosErr = e as any;
				if (axiosErr.response?.data?.detail) msg = axiosErr.response.data.detail;
				else if (axiosErr.message) msg = axiosErr.message;
			} else if (e instanceof Error) {
				msg = e.message;
			}
			displayMsg(msg, { type: "danger", position: "top", duration: 4000 });
		}
	};

	const handleStartEdit = () => {
		setEditNoteText(stripHtmlToText(note?.note ?? ""));
		setEditCategoryId(note?.pastoral_type_id ?? "");
		setEditTags(note?.tags.map((t) => t.name) ?? []);
		setIsEditing(true);
	};

	const handleSaveNote = async () => {
		if (!note) return;
		try {
			await updateNote({
				id: note.id,
				noteText: editNoteText,
				categoryId: editCategoryId || undefined,
				tags: editTags,
			});
			setIsEditing(false);
			displayMsg("Note saved.", { type: "success", position: "bottom", duration: 2000 });
		} catch (e: unknown) {
			let msg = "Failed to save note. Please try again.";
			if (e && typeof e === "object" && "response" in e) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const axiosErr = e as any;
				if (axiosErr.response?.data?.detail) msg = axiosErr.response.data.detail;
				else if (axiosErr.message) msg = axiosErr.message;
			} else if (e instanceof Error) {
				msg = e.message;
			}
			displayMsg(msg, { type: "danger", position: "top", duration: 4000 });
		}
	};

	const isOpen = !!note || isAdding;

	return (
		<Box
			sx={{
				// On wider screens split the grid/drawer 3/5 vs 2/5; keep the
				// slightly roomier 42% drawer on narrower screens where space is tight.
				flex: isOpen ? { xs: "0 0 42%", lg: "0 0 40%" } : "0 0 0",
				width: isOpen ? undefined : 0,
				overflow: "hidden",
				transition: "flex 0.3s ease, width 0.3s ease",
				borderLeft: isOpen ? "1px solid #e8ecf0" : "none",
				bgcolor: "background.paper",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{isAdding && !note && (
				<Box display="flex" flexDirection="column" sx={{ flex: 1, minHeight: 0 }}>
					{/* Add note header */}
					<Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #f0f0f0" }}>
						<Typography fontWeight={600} fontSize={15} flex={1}>New Note{studentName ? ` — ${studentName}` : ""}</Typography>
						<Button size="small" onClick={onAddClose} disabled={isCreating} sx={{ textTransform: "none" }}>Cancel</Button>
						<Button size="small" variant="contained" onClick={handleCreateNote}
							disabled={!newNoteText.trim() || isCreating}
							startIcon={isCreating ? <CircularProgress size={14} color="inherit" /> : undefined}
							sx={{ bgcolor: "#002f67", "&:hover": { bgcolor: "#001a4a" }, textTransform: "none" }}
						>
							{isCreating ? "Saving..." : "Save"}
						</Button>
					</Box>

					{/* Add note form */}
					<Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<Box display="flex" gap={2} alignItems="flex-start">
							<FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
								<Select value={newCategoryId} onChange={(e) => setNewCategoryId(e.target.value)} displayEmpty sx={{ fontSize: 13 }}>
									<MenuItem value="" sx={{ fontSize: 13 }}>Select type...</MenuItem>
									{categories?.map((c) => (
										<MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>{c.label}</MenuItem>
									))}
								</Select>
							</FormControl>

							<Autocomplete
								multiple
								size="small"
								sx={{ flex: 1, minWidth: 0 }}
								options={PREDEFINED_TAG_NAMES}
								value={newTags}
								onChange={(_, v) => setNewTags(v)}
								renderTags={(value, getTagProps) =>
									value.map((tag, index) => (
										<Chip label={tag} size="small" {...getTagProps({ index })} sx={{ bgcolor: tagColour(tag), color: "#fff", fontSize: 11 }} />
									))
								}
								renderInput={(params) => <TextField {...params} placeholder="Add tags..." sx={{ "& .MuiInputBase-input": { fontSize: 13 } }} />}
							/>
						</Box>

						<Box position="relative">
							<TextField multiline minRows={12} fullWidth size="small" placeholder="Write a note..." value={newNoteText}
								onChange={(e) => setNewNoteText(e.target.value)} sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
							/>
							{newNoteText.trim() && (
								<Box sx={{ position: "absolute", top: 6, right: 8, zIndex: 2 }}>
									{isGeneratingAi && aiTargetRef.current === "new" ? (
										<CircularProgress size={18} sx={{ color: "#002f67" }} />
									) : (
										<img
											src="https://pymblelc.blob.core.windows.net/assets/MagicIcon.svg"
											alt="AI Assistant"
											title="Use AI to help write this note"
											onClick={() => handleAiGenerate("new")}
											style={{ width: 22, height: 22, cursor: "pointer", display: "block" }}
										/>
									)}
								</Box>
							)}
						</Box>

						<Box>
							<Typography fontSize={11} fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} mb={0.75}>Attachments</Typography>
							{newUploadedFiles.length > 0 && (
								<Box display="flex" flexDirection="column" gap={0.75} mb={1}>
									{newUploadedFiles.map((file, i) => (
										<Box key={file.InternalName ?? i} display="flex" alignItems="center" gap={1} sx={{ px: 1.25, py: 0.75, borderRadius: 1, border: "1px solid #e8ecf0", bgcolor: "#f8f9fb" }}>
											{isPdf(file) ? <PictureAsPdfIcon sx={{ fontSize: 16, color: "#e53935", flexShrink: 0 }} /> : <InsertDriveFileIcon sx={{ fontSize: 16, color: "#74b9d4", flexShrink: 0 }} />}
											<Typography fontSize={12} flex={1} noWrap>{file.name}</Typography>
											<IconButton size="small" onClick={() => handleRemoveNewNoteFile(file)} disabled={isUploading}>
												<CloseIcon sx={{ fontSize: 14 }} />
											</IconButton>
										</Box>
									))}
								</Box>
							)}
							{isUploading && (
								<Box display="flex" alignItems="center" gap={1} mb={0.75}>
									<CircularProgress size={14} thickness={4} sx={{ color: "#002f67" }} />
									<Typography fontSize={12} color="text.secondary">Uploading...</Typography>
								</Box>
							)}
							<FileUploadZone files={newPendingFiles} onChange={handleNewNoteFileZoneChange} maxFileSize={20 * 1024 * 1024} placeholder="Click to upload or drag & drop" />
						</Box>
					</Box>
				</Box>
			)}
			{note && (
				<Box display="flex" flexDirection="column" sx={{ flex: 1, minHeight: 0 }}>
					{/* Header */}
					<Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
						<ContactProfileImage
							cssClass="pastoral-student-avatar"
							profile={{ contactId: note.student_id, name: note.student_name }}
						/>
						<Box flex={1} minWidth={0}>
							<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
								<Typography fontSize={15} fontWeight={700} noWrap>{note.student_name}</Typography>
								{note.student_year && (
									<Typography fontSize={12} color="text.secondary" noWrap>{note.student_year}</Typography>
								)}
								{isEditing ? (
									<FormControl size="small">
										<Select value={editCategoryId} onChange={(e) => setEditCategoryId(e.target.value)} disabled={isSaving}
											sx={{ fontSize: 12, height: 24, ".MuiSelect-select": { py: 0, px: 1 } }}
										>
											{categories?.map((c) => (
												<MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>{c.label}</MenuItem>
											))}
										</Select>
									</FormControl>
								) : (
									<Chip
										label={note.pastoral_type}
										size="small"
										sx={{ bgcolor: note.pastoral_type_colour ?? "#dde3f0", color: "#fff", fontWeight: 500, fontSize: 12, height: 22 }}
									/>
								)}
								{!isEditing && isChildProtection && <NoteStatusChip status={savedStatus} size="md" />}
							</Box>
							<Box display="flex" alignItems="center" gap={1.5} mt={0.25}>
								<Box display="flex" alignItems="center" gap={0.5}>
									<CalendarTodayIcon sx={{ fontSize: 12, color: "text.secondary" }} />
									<Typography fontSize={12} color="text.secondary">
										{new Date(note.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
									</Typography>
								</Box>
								{note.author && (
									<Box display="flex" alignItems="center" gap={0.5}>
										<PersonIcon sx={{ fontSize: 12, color: "text.secondary" }} />
										<Typography fontSize={12} color="text.secondary">{note.author}</Typography>
									</Box>
								)}
							</Box>
						</Box>
						<Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
							{isEditing ? (
								<>
									<Button size="small" onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</Button>
									<Button size="small" variant="contained" onClick={handleSaveNote} disabled={isSaving}>Save</Button>
								</>
							) : (
								<>
									<span className="pastoral-note-btn-wrapper">
										<IonButton fill="outline" size="small" color="primary" className="pastoral-note-action-btn" onClick={() => setCommentsOpen(true)} title="Comments" aria-label="Comments">
											<IonIcon icon={chatbubbleOutline} slot="icon-only" />
										</IonButton>
										{liveCommentCount > 0 && <span className="pastoral-note-badge">{liveCommentCount}</span>}
									</span>
									<span className="pastoral-note-btn-wrapper">
										<IonButton fill="outline" size="small" color="primary" className="pastoral-note-action-btn" onClick={() => setAttachmentsOpen(true)} title="Attachments" aria-label="Attachments">
											<IonIcon icon={attach} slot="icon-only" />
										</IonButton>
										{attachmentCount > 0 && <span className="pastoral-note-badge">{attachmentCount}</span>}
									</span>
									{isAuthor && (
										<IonButton fill="outline" size="small" color="primary" className="pastoral-note-action-btn" onClick={handleStartEdit} title="Edit note" aria-label="Edit note">
											<IonIcon icon={createOutline} slot="icon-only" />
										</IonButton>
									)}
									{canDeleteNote && (
										<IonButton fill="outline" size="small" color="danger" className="pastoral-note-action-btn" onClick={handleDeleteNote} disabled={isDeletingNote} title="Delete note" aria-label="Delete note">
											{isDeletingNote ? <CircularProgress size={14} thickness={5} /> : <IonIcon icon={trashOutline} slot="icon-only" />}
										</IonButton>
									)}
									{isChildProtection && (
										<IonButton fill={isActioned ? "solid" : "outline"} size="small" color={isActioned ? "success" : "primary"} className="pastoral-note-action-btn" onClick={handleToggleActioned} disabled={isSettingStatus} title={isActioned ? "Reopen this CP note" : "Mark this CP note as actioned"} aria-label={isActioned ? "Reopen note" : "Mark note actioned"}>
											<IonIcon icon={isActioned ? refreshOutline : checkmarkDoneOutline} slot="icon-only" />
										</IonButton>
									)}
									<IonButton fill="outline" size="small" color="primary" className="pastoral-note-action-btn" onClick={handleCopyLink} title="Copy link to this note" aria-label="Copy link to this note">
										<IonIcon icon={linkOutline} slot="icon-only" />
									</IonButton>
								</>
							)}
							<IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }} aria-label="Close">
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>
					</Box>

					{/* Details — always visible */}
					<Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2.5, borderTop: "1px solid #e8ecf0" }}>
						{isEditing ? (
							<Box position="relative">
								<TextField multiline fullWidth minRows={6} value={editNoteText} onChange={(e) => setEditNoteText(e.target.value)} disabled={isSaving} sx={{ "& .MuiInputBase-input": { fontSize: 13 } }} />
								{editNoteText.trim() && (
									<Box sx={{ position: "absolute", top: 6, right: 8, zIndex: 2 }}>
										{isGeneratingAi && aiTargetRef.current === "edit" ? (
											<CircularProgress size={18} sx={{ color: "#002f67" }} />
										) : (
											<img
												src="https://pymblelc.blob.core.windows.net/assets/MagicIcon.svg"
												alt="AI Assistant"
												title="Use AI to help write this note"
												onClick={() => handleAiGenerate("edit")}
												style={{ width: 22, height: 22, cursor: "pointer", display: "block" }}
											/>
										)}
									</Box>
								)}
							</Box>
						) : (
							<HtmlText htmltext={note.note ?? ""} cssClass="pa-summary-text-full" />
						)}
						<NoteVisibilityIndicator noteId={note.id} confidential={false} />
						{isEditing ? (
							<Box>
								<Typography fontSize={11} fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} mb={0.75}>Tags</Typography>
								<Autocomplete
									multiple freeSolo options={PREDEFINED_TAG_NAMES} value={editTags}
									onChange={(_, newValue) => setEditTags(newValue as string[])} disabled={isSaving}
									renderTags={(value, getTagProps) => value.map((option, index) => (
										<Chip {...getTagProps({ index })} key={option} label={`#${option}`} size="small" sx={{ bgcolor: tagColour(option), color: "#fff", fontWeight: 500, fontSize: 11 }} />
									))}
									renderInput={(params) => (
										<TextField {...params} size="small" placeholder={editTags.length === 0 ? "Add tags..." : ""} sx={{ "& .MuiInputBase-input": { fontSize: 13 } }} />
									)}
								/>
							</Box>
						) : (
							note.tags.length > 0 && (
								<Box>
									<Typography fontSize={11} fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} mb={0.75}>Tags</Typography>
									<Box display="flex" flexWrap="wrap" gap={0.75}>
										{note.tags.map((tag) => (
											<Chip key={tag.id} label={`#${tag.name}`} size="small" sx={{ bgcolor: tag.colour, color: "#fff", fontWeight: 500, fontSize: 11 }} />
										))}
									</Box>
								</Box>
							)
						)}
					</Box>
				</Box>
			)}

			{/* Comments modal */}
			<IonModal isOpen={commentsOpen} onDidDismiss={() => setCommentsOpen(false)} onDidPresent={() => commentInputRef.current?.focus()} className="pastoral-comments-modal">
				<IonHeader>
					<IonToolbar>
						<IonTitle>Comments</IonTitle>
						<IonButtons slot="end">
							<IonButton fill="clear" onClick={() => setCommentsOpen(false)} aria-label="Close">
								<IonIcon icon={closeOutline} />
							</IonButton>
						</IonButtons>
					</IonToolbar>
				</IonHeader>
				<IonContent>
					<Box sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
						{systemCommentCount > 0 && (
							<Box display="flex" justifyContent="flex-end" mb={1.5}>
								<FormControlLabel
									control={<Switch size="small" checked={showSystemComments} onChange={(e) => setShowSystemComments(e.target.checked)} />}
									label={<Typography fontSize={11} color="text.secondary">System ({systemCommentCount})</Typography>}
									labelPlacement="start" sx={{ mr: 0, ml: 0, gap: 0.5 }}
								/>
							</Box>
						)}
						{commentsLoading ? (
							<Box display="flex" justifyContent="center" py={4}><CircularProgress size={20} thickness={4} /></Box>
						) : !visibleComments || visibleComments.length === 0 ? (
							<Typography color="text.secondary" fontSize={13}>No comments yet.</Typography>
						) : (
							<Box display="flex" flexDirection="column" gap={1.5}>
								{visibleComments.map((c) => {
									// Only the comment's author may edit or delete it, and never a
									// system comment. UX gate only — the API enforces authorship.
									const isCommentAuthor = !c.isSystem && canEditEntity(c, myDetails);
									const isEditingThis = editingCommentId === c.id;
									const isSavingThis = savingCommentId === c.id;
									const isDeletingThis = deletingCommentId === c.id;
									return (
									<Box key={c.id} display="flex" gap={1} alignItems="flex-start" flexDirection={c.isSystem ? "row-reverse" : "row"} sx={{ opacity: isDeletingThis ? 0.5 : 1 }}>
										<ContactProfileImage cssClass="pastoral-comment-avatar" profile={{ contactId: c.author_id, name: c.author }} />
										<Box flex={1} minWidth={0}>
											<Box display="flex" alignItems="center" gap={0.5} mb={0.4} flexDirection={c.isSystem ? "row-reverse" : "row"}>
												<Typography fontSize={11} fontWeight={600} color="text.secondary">{c.author}</Typography>
												{c.confidential && <LockIcon sx={{ fontSize: 11, color: "text.disabled" }} titleAccess="Confidential" />}
												<Typography fontSize={10} color="text.disabled" ml={c.isSystem ? 0 : 0.5} mr={c.isSystem ? 0.5 : 0}>
													{new Date(c.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
												</Typography>
												{isCommentAuthor && !isEditingThis && (
													<Box display="flex" gap={0.25} ml={0.5}>
														<IconButton size="small" onClick={() => handleStartEditComment(c)} disabled={isDeletingThis} aria-label="Edit comment" title="Edit comment" sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "#002f67" } }}>
															<EditOutlinedIcon sx={{ fontSize: 14 }} />
														</IconButton>
														<IconButton size="small" onClick={() => handleDeleteComment(c.id)} disabled={isDeletingThis} aria-label="Delete comment" title="Delete comment" sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "error.main" } }}>
															{isDeletingThis ? <CircularProgress size={12} thickness={5} /> : <DeleteOutlineIcon sx={{ fontSize: 14 }} />}
														</IconButton>
													</Box>
												)}
											</Box>
											{isEditingThis ? (
												<Box>
													<TextField
														multiline minRows={2} maxRows={6} fullWidth size="small"
														value={editingCommentText}
														onChange={(e) => setEditingCommentText(e.target.value)}
														onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSaveComment(); }}
														disabled={isSavingThis} autoFocus
														sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
													/>
													<Box display="flex" gap={1} mt={0.75} justifyContent="flex-end">
														<Button size="small" onClick={handleCancelEditComment} disabled={isSavingThis} sx={{ textTransform: "none", fontSize: 12 }}>Cancel</Button>
														<Button size="small" variant="contained" onClick={handleSaveComment}
															disabled={!editingCommentText.trim() || isSavingThis}
															startIcon={isSavingThis ? <CircularProgress size={12} color="inherit" /> : undefined}
															sx={{ bgcolor: "#002f67", "&:hover": { bgcolor: "#001a4a" }, textTransform: "none", fontSize: 12 }}
														>
															{isSavingThis ? "Saving..." : "Save"}
														</Button>
													</Box>
												</Box>
											) : (
											<Box display="flex" justifyContent={c.isSystem ? "flex-end" : "flex-start"}>
												<Box className={c.isSystem ? "comment-bubble comment-bubble--system" : "comment-bubble"}>
													<Typography fontSize={13} lineHeight={1.5} sx={{ whiteSpace: "pre-wrap" }}>{c.text}</Typography>
													{c.files && c.files.length > 0 && (
														<Box display="flex" flexDirection="column" gap={0.5} mt={0.75}>
															{c.files.map((file, fi) => (
																<Box key={file.InternalName ?? fi} display="flex" alignItems="center" gap={0.75} onClick={() => openFile(file)} className="comment-bubble-file">
																	{isPdf(file) ? <PictureAsPdfIcon sx={{ fontSize: 14, color: "#e53935", flexShrink: 0 }} /> : <InsertDriveFileIcon sx={{ fontSize: 14, color: "#5a8fbf", flexShrink: 0 }} />}
																	<Typography fontSize={11} noWrap flex={1}>{file.name}</Typography>
																</Box>
															))}
														</Box>
													)}
												</Box>
											</Box>
											)}
										</Box>
									</Box>
									);
								})}
							</Box>
						)}
						{isSubmittingComment && (
							<Box display="flex" gap={1} alignItems="flex-start" mt={1.5} sx={{ opacity: 0.5 }}>
								<CircularProgress size={26} thickness={2} sx={{ color: "#74b9d4", flexShrink: 0, mt: "2px" }} />
								<Box flex={1} minWidth={0}>
									<Box className="comment-bubble" sx={{ fontStyle: "italic" }}>
										<Typography fontSize={13} color="text.secondary">{commentText || "Sending..."}</Typography>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</IonContent>
				<IonFooter>
					<Box sx={{ borderTop: "1px solid #e8ecf0", p: 2 }}>
						{commentUploadedFiles.length > 0 && (
							<Box display="flex" flexDirection="column" gap={0.5} mb={1}>
								{commentUploadedFiles.map((file, i) => (
									<Box key={file.InternalName ?? i} display="flex" alignItems="center" gap={1} sx={{ px: 1, py: 0.5, borderRadius: 1, border: "1px solid #e8ecf0", bgcolor: "#f8f9fb" }}>
										{isPdf(file) ? <PictureAsPdfIcon sx={{ fontSize: 16, color: "#e53935", flexShrink: 0 }} /> : <InsertDriveFileIcon sx={{ fontSize: 16, color: "#74b9d4", flexShrink: 0 }} />}
										<Typography fontSize={12} flex={1} noWrap>{file.name}</Typography>
										<IconButton size="small" onClick={() => handleRemoveCommentFile(file)}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
									</Box>
								))}
							</Box>
						)}
						{showCommentUpload && (
							<Box mb={1}>
								{isUploading && (
									<Box display="flex" alignItems="center" gap={1} mb={0.5}>
										<CircularProgress size={14} thickness={4} sx={{ color: "#002f67" }} />
										<Typography fontSize={12} color="text.secondary">Uploading...</Typography>
									</Box>
								)}
								<FileUploadZone files={commentPendingFiles} onChange={handleCommentFileZoneChange} maxFileSize={20 * 1024 * 1024} placeholder="Click to upload or drag & drop" />
							</Box>
						)}
						{/* Who can see this — reflects the comment's confidential toggle,
						    anchored to the parent note (same as my-pymble-portal). */}
						{note && commentConfidential && (
							<Box mb={1}>
								<NoteVisibilityIndicator noteId={note.id} confidential={commentConfidential} category={note.pastoral_type_id} />
							</Box>
						)}
						<TextField multiline minRows={2} maxRows={4} fullWidth size="small" placeholder="Add a comment..." value={commentText}
							onChange={(e) => setCommentText(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmitComment(); }}
							disabled={isSubmittingComment} sx={{ mb: 1 }}
							inputRef={commentInputRef}
						/>
						<Box display="flex" alignItems="center" gap={1}>
							<IconButton size="small" onClick={() => setShowCommentUpload((prev) => !prev)} sx={{ color: showCommentUpload ? "#002f67" : "text.secondary" }} aria-label="Attach file">
								<AttachFileIcon fontSize="small" />
							</IconButton>
							{isChildProtection && (
								<FormControlLabel
									control={<Switch size="small" checked={commentConfidential} onChange={(e) => setCommentConfidential(e.target.checked)} />}
									label={<Typography fontSize={11} color="text.secondary">Confidential</Typography>}
									labelPlacement="start" sx={{ mr: 0, ml: 0, gap: 0.25 }}
								/>
							)}
							<Box flex={1} />
							<Button size="small" variant="contained" onClick={handleSubmitComment}
								disabled={(!commentText.trim() && commentUploadedFiles.length === 0) || isSubmittingComment || isUploading}
								startIcon={isSubmittingComment ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
								sx={{ bgcolor: "#002f67", "&:hover": { bgcolor: "#001a4a" }, textTransform: "none", fontSize: 13 }}
							>
								{isSubmittingComment ? "Posting..." : "Post"}
							</Button>
						</Box>
					</Box>
				</IonFooter>
			</IonModal>

			{/* Attachments modal */}
			<IonModal isOpen={attachmentsOpen} onDidDismiss={() => setAttachmentsOpen(false)} className="pastoral-attachments-modal">
				<IonHeader>
					<IonToolbar>
						<IonTitle>Attachments</IonTitle>
						<IonButtons slot="end">
							<IonButton fill="clear" onClick={() => setAttachmentsOpen(false)} aria-label="Close">
								<IonIcon icon={closeOutline} />
							</IonButton>
						</IonButtons>
					</IonToolbar>
				</IonHeader>
				<IonContent>
					<Box sx={{ p: 2 }}>
						{displayFiles.length > 0 && (
							<Box display="flex" flexDirection="column" gap={1} mb={2}>
								{displayFiles.map((file, i) => (
									<Box key={file.InternalName ?? i} display="flex" alignItems="center" gap={1.5} sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid #e8ecf0" }}>
										<Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0} onClick={() => openFile(file)} sx={{ cursor: "pointer", "&:hover .file-name": { color: "#002f67" } }}>
											{canShowThumb(file) ? (
												<Box
													component="img"
													src={thumbUrl(file)}
													alt={file.name}
													loading="lazy"
													onError={() => setBrokenThumbs((prev) => new Set(prev).add(thumbKey(file)))}
													sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover", flexShrink: 0, bgcolor: "#f0f4f8" }}
												/>
											) : isPdf(file) ? (
												<PictureAsPdfIcon sx={{ fontSize: 26, color: "#e53935", flexShrink: 0 }} />
											) : (
												<InsertDriveFileIcon sx={{ fontSize: 26, color: "#74b9d4", flexShrink: 0 }} />
											)}
											<Box minWidth={0} flex={1}>
												<Typography className="file-name" fontSize={13} fontWeight={500} noWrap>{file.name}</Typography>
												{fileUploaderCaption(file) && (
													<Typography fontSize={11} color="text.secondary" noWrap>{fileUploaderCaption(file)}</Typography>
												)}
											</Box>
										</Box>
										{file.InternalName && (
											<IconButton size="small" onClick={() => handleDeleteFile(file)} disabled={isDeleting} aria-label={`Delete ${file.name}`} sx={{ color: "text.disabled", flexShrink: 0, "&:hover": { color: "error.main" } }}>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										)}
									</Box>
								))}
							</Box>
						)}
						{isUploading && (
							<Box display="flex" alignItems="center" gap={1.5} sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "#f0f4ff", mb: 2 }}>
								<CircularProgress size={16} thickness={4} sx={{ color: "#002f67" }} />
								<Typography fontSize={13} color="text.secondary">Uploading...</Typography>
							</Box>
						)}
						<FileUploadZone files={pendingFiles} onChange={handleFileZoneChange} maxFileSize={20 * 1024 * 1024} placeholder="Click to upload or drag & drop" />
					</Box>
				</IonContent>
			</IonModal>
		</Box>
	);
};

export default PastoralNoteDrawer;
